import { useState } from 'react';

type Hour = { hour: number; inbound: number; outbound: number; total: number };
type Day = { date: string; inbound: number; outbound: number };
type Analytics = {
  window: { days: number; months: number };
  totals: { calls: number; inbound: number; outbound: number; total_minutes: number };
  averages: { per_hour: number; per_day: number; per_month: number; inbound_per_day: number; outbound_per_day: number };
  hour_profile: Hour[]; busy_hour: { hour: number; avg_calls: number };
  peak_concurrent: { max: number; at: string | null };
  daily: Day[]; monthly: { month: string; inbound: number; outbound: number }[];
  by_flow?: { flow: string; label: string; calls: number; answered: number; per_day: number }[];
  outcomes?: { answered: number; missed: number; abandoned: number };
  total_calls?: number; public_safety_calls?: number;
};

function Tile({ v, l }: { v: any; l: string }) {
  return <div className="card p-3"><div className="text-xl font-semibold">{v}</div><div className="text-[11px] text-gray-500">{l}</div></div>;
}
function hourLabel(h: number) { const ap = h < 12 ? 'a' : 'p'; const hr = h % 12 === 0 ? 12 : h % 12; return `${hr}${ap}`; }

export default function CdrAnalytics({ departments, dept, setDept }: { departments: string[]; dept: string; setDept: (v: string) => void }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [a, setA] = useState<Analytics | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setBusy(true); setErr(null);
    const p: Record<string, string> = {};
    if (from) p.from = from; if (to) p.to = to; if (dept) p.dept = dept;
    try {
      const r = await fetch('/cdr/api/analytics?' + new URLSearchParams(p).toString(), { credentials: 'include' });
      if (r.status === 401) { setErr('Sign in to the CDR portal to view.'); setA(null); return; }
      if (!r.ok) { setErr('Failed (' + r.status + ')'); setA(null); return; }
      setA(await r.json());
    } catch (e: any) { setErr(e?.message ?? 'failed'); } finally { setBusy(false); }
  }

  const inp = 'px-2.5 py-1.5 rounded-md border border-tnh-border dark:border-tnh-border-dark bg-transparent text-xs';
  const hpMax = a ? Math.max(1, ...a.hour_profile.map(h => h.total)) : 1;
  const dMax = a ? Math.max(1, ...a.daily.map(d => d.inbound + d.outbound)) : 1;
  return (
    <div className="space-y-3">
      <div className="card p-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-5 items-end">
        <select value={dept} onChange={(e) => setDept(e.target.value)} className={inp}><option value="">All departments</option>{departments.map(d => <option key={d} value={d}>{d}</option>)}</select>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={inp} />
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={inp} />
        <button onClick={load} disabled={busy} className="px-3 py-1.5 rounded-md bg-tnh-accent text-white text-xs disabled:opacity-50">{busy ? 'Loading…' : 'Run analytics'}</button>
      </div>
      {err && <div className="card p-3 text-sm text-tnh-err">{err}</div>}
      {!a && !err && <div className="card p-6 text-center text-gray-500 text-sm">Pick a date range and Run analytics.</div>}
      {a && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Tile v={a.totals.calls.toLocaleString()} l="Total calls" />
            <Tile v={a.totals.inbound.toLocaleString()} l="Inbound" />
            <Tile v={a.totals.outbound.toLocaleString()} l="Outbound" />
            <Tile v={a.totals.total_minutes.toLocaleString()} l="Total minutes" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Tile v={a.averages.per_hour} l="Avg calls / hour" />
            <Tile v={a.averages.per_day} l="Avg calls / day" />
            <Tile v={a.averages.per_month} l="Avg calls / month" />
            <Tile v={`${hourLabel(a.busy_hour.hour)} (${a.busy_hour.avg_calls})`} l="Busy hour (avg calls)" />
            <Tile v={a.peak_concurrent.max} l="Peak concurrent calls" />
          </div>
          <div className="card p-3">
            <div className="text-sm font-semibold mb-2">Average calls by hour of day (Pacific) — inbound + outbound</div>
            <div className="flex items-end gap-1 h-40">
              {a.hour_profile.map((h) => (
                <div key={h.hour} className="flex-1 flex flex-col items-center justify-end" title={`${hourLabel(h.hour)}: in ${h.inbound}, out ${h.outbound}`}>
                  <div className="w-full flex flex-col justify-end" style={{ height: `${(h.total / hpMax) * 100}%` }}>
                    <div style={{ height: `${(h.outbound / (h.total || 1)) * 100}%`, background: '#b8500f' }} />
                    <div style={{ height: `${(h.inbound / (h.total || 1)) * 100}%`, background: '#0a7d33' }} />
                  </div>
                  <div className="text-[9px] text-gray-500 mt-1">{h.hour % 3 === 0 ? hourLabel(h.hour) : ''}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-gray-500 mt-1"><span style={{ color: '#0a7d33' }}>■</span> Inbound &nbsp; <span style={{ color: '#b8500f' }}>■</span> Outbound &nbsp;·&nbsp; peak concurrent {a.peak_concurrent.max}{a.peak_concurrent.at ? ` at ${new Date(a.peak_concurrent.at).toLocaleString()}` : ''}</div>
          </div>
          {a.by_flow && a.by_flow.length > 0 && (
            <div className="grid gap-2 md:grid-cols-2">
              <div className="card p-3">
                <div className="text-sm font-semibold mb-2">Calls by flow (per call){a.public_safety_calls ? <span className="ml-2 px-1.5 py-0.5 rounded bg-red-600 text-white text-[10px] font-semibold">911: {a.public_safety_calls}</span> : null}</div>
                <div className="space-y-1.5">
                  {a.by_flow.map((f) => {
                    const max = Math.max(1, ...a.by_flow!.map(x => x.calls));
                    return (
                      <div key={f.flow} className="flex items-center gap-2 text-xs">
                        <div className="w-28 shrink-0 text-gray-600 dark:text-gray-300">{f.label}</div>
                        <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded h-4 overflow-hidden">
                          <div className="h-full bg-tnh-accent" style={{ width: `${(f.calls / max) * 100}%` }} />
                        </div>
                        <div className="w-24 text-right tabular-nums">{f.calls.toLocaleString()} <span className="text-gray-400">({f.per_day}/d)</span></div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {a.outcomes && (
                <div className="card p-3">
                  <div className="text-sm font-semibold mb-2">Call outcomes (per call)</div>
                  <div className="grid grid-cols-3 gap-2">
                    <Tile v={a.outcomes.answered.toLocaleString()} l="Answered" />
                    <Tile v={a.outcomes.missed.toLocaleString()} l="Missed" />
                    <Tile v={a.outcomes.abandoned.toLocaleString()} l="Abandoned" />
                  </div>
                  <div className="text-[11px] text-gray-500 mt-2">Total distinct calls: {(a.total_calls ?? 0).toLocaleString()}{a.total_calls ? ` · answer rate ${Math.round((a.outcomes.answered / a.total_calls) * 100)}%` : ''}</div>
                </div>
              )}
            </div>
          )}
          {a.daily.length > 1 && (
            <div className="card p-3">
              <div className="text-sm font-semibold mb-2">Daily call volume</div>
              <div className="flex items-end gap-[2px] h-32">
                {a.daily.map((d) => (
                  <div key={d.date} className="flex-1" title={`${d.date}: in ${d.inbound}, out ${d.outbound}`}>
                    <div style={{ height: `${((d.inbound + d.outbound) / dMax) * 100}%`, background: '#0064ff', borderRadius: '2px 2px 0 0' }} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {a.monthly.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-4 py-2 border-b border-tnh-border dark:border-tnh-border-dark text-sm font-semibold">Monthly</div>
              <table className="w-full text-xs"><thead className="bg-gray-50 dark:bg-gray-900/40"><tr><th className="text-left px-3 py-1.5">Month</th><th className="text-right px-3 py-1.5">Inbound</th><th className="text-right px-3 py-1.5">Outbound</th><th className="text-right px-3 py-1.5">Total</th></tr></thead>
                <tbody>{a.monthly.map(m => <tr key={m.month} className="border-t border-tnh-border/40 dark:border-tnh-border-dark/40"><td className="px-3 py-1.5 font-mono">{m.month}</td><td className="px-3 py-1.5 text-right">{m.inbound}</td><td className="px-3 py-1.5 text-right">{m.outbound}</td><td className="px-3 py-1.5 text-right font-medium">{m.inbound + m.outbound}</td></tr>)}</tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
